import React from 'react'
import { useNavigate } from 'react-router-dom'
import { EarlierVisit } from '../types/review'
import { VisitType, getVisitTypeDisplayLabel } from '../constants/visitType'
import { formatScoreOneDecimal } from '../utils/scoreFormat'
import PrivateBadge from './PrivateBadge'
import ListPhoto from './ListPhoto'

interface VisitThreadProps {
  visits: EarlierVisit[]
  /**
   * 대표 기록에 적용된 점수 가리기 판정.
   *
   * 이전 회차도 같은 사람이 같은 분류에 남긴 것이라 판정이 같다. 대표 카드가
   * '? 점'인데 바로 아래 스레드에 숫자가 보이면 가린 의미가 없다.
   */
  hideScore?: boolean
}

/**
 * 같은 장소의 이전 회차들 — 본문 아래에 스레드로 이어 붙인다.
 *
 * 순서
 * ----
 * 서버가 최신→오래된 순으로 보내주므로 그대로 그린다. 본문(카드 위쪽)은 가장
 * 최근 회차이고, 아래로 갈수록 예전 기록이다.
 *
 * 시간순(1번째가 위)이 아닌 이유는 카드 맨 위 점수와 맞추기 위해서다. 맨 위
 * 점수는 이 장소의 대표 점수(=최신 회차)인데, 바로 아래에 1번째 기록이 오면
 * 두 숫자가 달라 무엇을 가리키는지 알 수 없다.
 *
 * 프로필·장소 이름을 반복하지 않는다
 * --------------------------------
 * 같은 사람이 같은 장소에 남긴 기록이라 헤더를 되풀이할 이유가 없다.
 * 회차와 점수만 있으면 어떤 기록인지 알 수 있다.
 */
const VisitThread: React.FC<VisitThreadProps> = ({ visits, hideScore }) => {
  const navigate = useNavigate()

  /*
   * 이전 회차도 최신 회차와 똑같이 열린다.
   *
   * 전에는 이 스레드에 onClick 이 하나도 없었고 사진에는 pointer-events-none
   * 까지 걸려 있어서, 가장 최근 기록만 열 수 있고 그 아래 회차는 눈으로만
   * 볼 수 있었다. EarlierVisit 에 제 id 가 들어 있고 서버도 1번째 기록을
   * 직접 여는 것을 지원하므로(preloadEarlierVisits 의 wholeGroup), 최신 회차와
   * 같은 주소로 보내면 된다.
   *
   * 사진의 pointer-events-none 은 그대로 둔다. 클릭은 감싼 칸이 받고,
   * 이미지는 끌기·길게눌러 저장이 막힌 채로 남는다.
   */
  const openDetail = (visitId: string) => navigate(`/review-detail/${visitId}`)
  const openPhoto = (visitId: string, index: number) =>
    navigate(`/review-photo/${visitId}/${index}`)

  if (visits.length === 0) return null

  const LINE_COLOR = 'rgba(75, 54, 33, 0.2)' // divider-divider-1

  /*
   * 점과 선이 만나는 자리.
   *
   * 선이 점 뒤를 지나가면 안 된다 — 점이 반투명이라 선이 비쳐 한가운데에
   * 진한 줄이 생기고, 점이 '선 위의 구슬'이 아니라 '선에 뚫린 자국'처럼
   * 보인다. 그래서 선을 점 위에서 끊고 점 아래에서 다시 시작한다.
   * 사이를 띄우지는 않는다 — 맞닿아 있어야 하나로 이어져 보인다.
   *
   * DOT_TOP = 위 padding 12px + (라벨 줄 20px - 점 8px) / 2
   */
  const DOT_TOP = 18
  const DOT_SIZE = 8
  const DOT_BOTTOM = DOT_TOP + DOT_SIZE

  return (
    <>
      {visits.map((v, i) => {
        const isFirst = i === 0
        const isLast = i === visits.length - 1

        // 날짜 줄 — 마지막 회차만 선 밖으로 뺀다 (아래 주석 참고)
        const dateRow = (
          <div className="w-full mt-10px flex items-center py-4px relative">
            {/*
              마지막 회차의 선 — 이 줄 한가운데에서 끝난다.

              bottom을 50%로 잡는 이유: 이 줄의 높이는 자물쇠(나만 보기)가
              붙느냐에 따라 달라진다. 고정값(14px 등)으로 맞추면 어떤 카드에서는
              가운데가 아니게 된다. 50%는 항상 정확히 가운데다.

              top: -10px 는 위 여백(mt-10px)까지 덮어, 본문에서 내려오던 선과
              끊기지 않게 이어붙이는 값이다.
              left: -18px 는 바깥 테두리 자리 (padding 16px + 테두리 2px).
            */}
            {isLast && (
              <span
                className="absolute w-2px"
                style={{
                  left: '-18px',
                  top: '-10px',
                  bottom: '50%',
                  background: LINE_COLOR,
                }}
              />
            )}
            <span className="flex-1 text-style-captionM text-textColorText3 text-left flex items-center">
              {!v.isPublic && (
                <>
                  <PrivateBadge />
                  <span className="mx-5px">·</span>
                </>
              )}
              {v.dateDisplay || v.date}
            </span>
            <span className="text-style-captionM text-textColorText3 text-right">
              {getVisitTypeDisplayLabel(v.visitType as VisitType)}
            </span>
          </div>
        )

        return (
          /*
            오른쪽 여백을 주지 않는다.
            카드(FeedReviewBox)가 이미 px-20px를 갖고 있어서, 여기서 mr-20px를
            더하면 오른쪽만 40px이 된다. 그러면 스레드의 점수·이용형태가 맨 위
            본문보다 안쪽으로 밀려 오른쪽 끝이 어긋난다.
            왼쪽은 스레드를 들여쓰기 위해 일부러 준다.
          */
          <div
            key={v.id}
            className="ml-20px cursor-pointer"
            onClick={(e) => {
              /*
                반드시 막아야 한다. 피드 카드(FeedReviewBox)의 바깥 칸에도
                '자세히보기' 클릭이 걸려 있어서, 안 막으면 이 회차로 갔다가
                곧바로 대표(최신) 회차로 덮어써진다 — 누른 회차가 안 열린다.
              */
              e.stopPropagation()
              openDetail(v.id)
            }}
          >
            {/*
              세로선은 회차 사이를 이어야 한다 — 끊기면 따로 노는 카드처럼 보인다.
              그래서 마지막이 아닌 회차는 날짜 줄까지 선 안에 두고, 위쪽 간격도
              margin이 아니라 padding으로 줘서 선이 끊기지 않게 한다.

              마지막 회차에서만 날짜 줄을 선 밖으로 뺀다. 선이 끝까지 내려오면
              아래로 더 이어질 것처럼 보이는데 거기가 끝이기 때문이다.
              (divider-divider-1 = rgba(75,54,33,.2))
            */}
            <div
              className="pl-16px pt-12px relative"
              /*
                선은 테두리가 아니라 조각 둘로 그린다.
                테두리는 박스를 처음부터 끝까지 한 줄로 긋기 때문에 점 자리를
                비울 수가 없다. 투명 테두리만 남기는 건 박스 크기를 그대로
                두기 위해서다 (없애면 안쪽 내용이 2px 왼쪽으로 밀린다).
              */
              style={{ borderLeft: '2px solid transparent' }}
            >
              {/*
                점 위 조각 — 위 회차에서 내려온 선을 점까지 잇는다.
                첫 회차는 위에서 내려오는 것이 없으므로 그리지 않는다.
              */}
              {!isFirst && (
                <span
                  className="absolute w-2px"
                  style={{
                    left: '-2px',
                    top: 0,
                    height: `${DOT_TOP}px`,
                    background: LINE_COLOR,
                  }}
                />
              )}

              {/* 점 아래 조각 — 점에서 다음 회차까지 */}
              <span
                className="absolute w-2px"
                style={{
                  left: '-2px',
                  top: `${DOT_BOTTOM}px`,
                  bottom: 0,
                  background: LINE_COLOR,
                }}
              />

              {/*
                점은 '회차 라벨' 줄에 맞춘다. 라벨이 이 회차의 시작이라
                거기 붙어야 무엇을 가리키는지 바로 읽힌다.
              */}
              <span
                className="absolute -left-5px rounded-full bg-brandPrimary-30%"
                style={{ top: `${DOT_TOP}px`, width: DOT_SIZE, height: DOT_SIZE }}
              />

              <div className="w-full flex items-center">
                <span className="flex-1 text-style-captionM text-textColorText3 text-left">
                  {v.visitNumber}번째 기록
                </span>
                {hideScore ? (
                  <span className="font-pretendard font-bold text-16px text-brown-80%-main text-right">
                    ? 점
                  </span>
                ) : (
                  v.score !== null && (
                    <span className="font-pretendard font-bold text-16px text-brown-80%-main text-right">
                      {formatScoreOneDecimal(v.score)} 점
                    </span>
                  )
                )}
              </div>

              {v.images.length > 0 && (
                <div className="w-full mt-10px flex gap-8px overflow-x-auto scrollbar-hide">
                  {v.images.map((image, index) => (
                    <div
                      key={index}
                      className="shrink-0 w-104px h-130px rounded-16px overflow-hidden bg-brandPrimary-5% cursor-pointer"
                      onClick={(e) => {
                        // 안 막으면 감싼 칸의 '자세히보기'까지 같이 열린다
                        e.stopPropagation()
                        openPhoto(v.id, index)
                      }}
                    >
                      <ListPhoto
                        src={image}
                        alt={`${v.visitNumber}번째 기록 이미지 ${index + 1}`}
                        className="w-full h-full object-cover pointer-events-none"
                        draggable={false}
                      />
                    </div>
                  ))}
                </div>
              )}

              {v.reviewText && (
                <p className="w-full mt-10px text-style-captionM text-textColorText2 text-left whitespace-pre-wrap">
                  {v.reviewText}
                </p>
              )}

              {!isLast && dateRow}
            </div>

            {/*
              마지막 회차의 날짜 줄. 투명 테두리를 두는 건 두 가지 때문이다.
               - 위 본문과 글자 시작 위치를 맞춘다 (테두리 2px만큼 밀리지 않게)
               - 위에서 그리는 선의 기준 위치(-18px)가 본문과 같아진다
            */}
            {isLast && (
              <div
                className="pl-16px"
                style={{ borderLeft: '2px solid transparent' }}
              >
                {dateRow}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

export default VisitThread
